import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { AgreementReadModel, useGetAgreementsQuery } from '@client/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  isDefinedNumber,
  isDefinedString,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  Text,
  toIntervalEndDateString,
  toIntervalStartDateString,
} from '@client/toolkit';

import { PageContentAnimation } from '../../../../components/layout';
import { useAgreementPartiesNumerationText } from '../../../agreements/hooks/use-agreement-parties-numeration-text';
import { colors } from '../../../../styles/color-tokens';

export function ProductAgreementsTab() {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const { id } = useParams<{ id: string }>();

  const pageSize = 10;
  const [page, setPage] = useState(0);

  const { data, isFetching } = useGetAgreementsQuery(
    {
      'productId.Equal': id,
      'pagination.Limit': pageSize,
      'pagination.Offset': page * pageSize,
    },
    { skip: id == null },
  );

  const agreements = useMemo(() => data?.items ?? [], [data?.items]);
  const hasAgreements = Array.isArray(agreements) && agreements.length > 0;
  const totalCount = data?.totalCount ?? 0;

  return (
    <PageContentAnimation>
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex-1" variant="dense">
            {tGlobal('legalPerson.rentalAgreements')}
            <Text
              as="span"
              className="inline-block align-top ml-1 font-light"
              loading={isFetching}
              isEmpty={!isDefinedNumber(totalCount)}
              emptyValue={0}
              SkeletonProps={{ className: 'min-w-[20px]' }}
            >
              ({totalCount})
            </Text>
          </CardTitle>
        </CardHeader>

        <CardContent variant="fill">
          <Table className="overflow-hidden rounded-md">
            {!isFetching && !hasAgreements && (
              <caption className="px-4 py-4 [caption-side:bottom]">
                <EmptyPlaceholder>
                  {tCommon('noEntityFound', { entity: tGlobal('entity.agreement', { context: 'plural' }) })}
                </EmptyPlaceholder>
              </caption>
            )}

            <TableHead>
              <TableHeadRow>
                <TableHeadCell>{tType('RelationshipType.Tenant')}</TableHeadCell>
                <TableHeadCell>{tType('RelationshipType.Owner')}</TableHeadCell>
                <TableHeadCell>{tGlobal('agreement.name')}</TableHeadCell>
                <TableHeadCell>{tGlobal('agreement.durationStart')}</TableHeadCell>
                <TableHeadCell>{tGlobal('agreement.durationEnd')}</TableHeadCell>
                <TableHeadCell>{tGlobal('agreement.status')}</TableHeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {isFetching && Array.from(Array(5).keys()).map((x) => <ProductAgreementRow key={x} isFetching />)}

              {!isFetching &&
                hasAgreements &&
                agreements.map((agreement) => (
                  <ProductAgreementRow key={agreement.id} agreement={agreement} isFetching={isFetching} />
                ))}
            </TableBody>
          </Table>
          <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
        </CardContent>
      </Card>
    </PageContentAnimation>
  );
}

interface ProductAgreementRowProps {
  agreement?: AgreementReadModel;
  isFetching?: boolean;
}

function ProductAgreementRow({ agreement, isFetching }: ProductAgreementRowProps) {
  const navigate = useNavigate();
  const { t: tCommon } = useTranslation();
  const { t: tType } = useTranslation('type');

  const owners = useAgreementPartiesNumerationText(agreement, 'Owner');
  const renters = useAgreementPartiesNumerationText(agreement, 'Tenant');

  const handleClick = () => {
    if (agreement?.id) {
      navigate(`/agreements/${agreement.id}`);
    }
  };

  return (
    <TableRow onClick={handleClick} className="cursor-pointer hover:bg-surface-secondary">
      <TableCell>
        <Text
          isEmpty={!isDefinedString(renters)}
          loading={isFetching}
          emptyValue={<span className="italic">{tCommon('none')}</span>}
        >
          {renters}
        </Text>
      </TableCell>
      <TableCell>
        <Text
          isEmpty={!isDefinedString(owners)}
          loading={isFetching}
          emptyValue={<span className="italic">{tCommon('none')}</span>}
        >
          {owners}
        </Text>
      </TableCell>
      <TableCell>
        <Text className="text-brand-primary" loading={isFetching} isEmpty={!isDefinedString(agreement?.name)}>
          {agreement?.name}
        </Text>
      </TableCell>
      <TableCell>
        <Text isEmpty={!isDefinedString(toIntervalStartDateString(agreement?.duration))} loading={isFetching}>
          {toIntervalStartDateString(agreement?.duration)}
        </Text>
      </TableCell>
      <TableCell>
        <Text isEmpty={!isDefinedString(toIntervalEndDateString(agreement?.duration))} loading={isFetching}>
          {toIntervalEndDateString(agreement?.duration)}
        </Text>
      </TableCell>
      <TableCell>
        <Text
          isEmpty={!isDefinedString(agreement?.status)}
          loading={isFetching}
          className={agreement?.status === 'Effective' ? `text-[${colors.status.success}]` : undefined}
        >
          {tType(`AgreementStatus.${agreement?.status}`)}
        </Text>
      </TableCell>
    </TableRow>
  );
}
